<template>
  <div class="activity-center-wrapper">
    <div
      class="activity-center-backdrop"
      role="presentation"
      @click="$emit('close')"
    ></div>

    <mi-drawer
      :model-value="true"
      overlay
      bordered
      side="right"
      :width="789"
    >
      <div class="activity-center">
        <activity-center-header
          @close="$emit('close')"
          @open:preferences="togglePreferences"
        ></activity-center-header>

        <activity-center-preferences
          v-if="isPreferencesOpen"
          @close="togglePreferences"
        ></activity-center-preferences>

        <div class="activity-center__content">
          <mi-inner-loading :showing="activitiesLoading">
            <mi-spinner size="70px"></mi-spinner>
          </mi-inner-loading>

          <template v-if="!activitiesLoading">
            <activity-center-tabs
              v-model="activeTab"
              :tabs="activityCenterTabs"
            ></activity-center-tabs>

            <mi-tab-panels
              v-model="activeTab"
              class="activity-center__panels"
              keep-alive
            >
              <mi-tab-panel :name="ACTIVITY_CENTER_TAB_NAMES.ALL">
                <slot name="all">
                  <activity-center-tab-content
                    v-model="activeFeature"
                    :status="ACTIVITY_CENTER_TAB_NAMES.ALL"
                    :activities="activities"
                    :focused-notification-id="notificationId"
                    :open-unread-only="!notificationId"
                    :initially-open-section-keys="initiallyOpenSectionKeys"
                    @navigate="$emit('close')"
                    @retry="handleRetry"
                    @retry:error="$emit('close')"
                  ></activity-center-tab-content>
                </slot>
              </mi-tab-panel>

              <mi-tab-panel :name="ACTIVITY_CENTER_TAB_NAMES.FAILED">
                <slot name="failed">
                  <activity-center-tab-content
                    v-model="activeFeature"
                    :status="ACTIVITY_CENTER_TAB_NAMES.FAILED"
                    :activities="activities"
                    :focused-notification-id="notificationId"
                    :open-unread-only="!notificationId"
                    :initially-open-section-keys="initiallyOpenSectionKeys"
                    @navigate="$emit('close')"
                    @retry="handleRetry"
                    @retry:error="$emit('close')"
                  ></activity-center-tab-content>
                </slot>
              </mi-tab-panel>

              <mi-tab-panel :name="ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS">
                <slot name="in-progress">
                  <activity-center-tab-content
                    v-model="activeFeature"
                    :status="ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS"
                    :activities="activities"
                    :focused-notification-id="notificationId"
                    :open-unread-only="!notificationId"
                    :initially-open-section-keys="initiallyOpenSectionKeys"
                    @navigate="$emit('close')"
                    @retry="handleRetry"
                    @retry:error="$emit('close')"
                  ></activity-center-tab-content>
                </slot>
              </mi-tab-panel>

              <mi-tab-panel :name="ACTIVITY_CENTER_TAB_NAMES.COMPLETED">
                <slot name="completed">
                  <activity-center-tab-content
                    v-model="activeFeature"
                    :status="ACTIVITY_CENTER_TAB_NAMES.COMPLETED"
                    :activities="activities"
                    :focused-notification-id="notificationId"
                    :open-unread-only="!notificationId"
                    :initially-open-section-keys="initiallyOpenSectionKeys"
                    @navigate="$emit('close')"
                    @retry="handleRetry"
                    @retry:error="$emit('close')"
                  ></activity-center-tab-content>
                </slot>
              </mi-tab-panel>
            </mi-tab-panels>
          </template>
        </div>
      </div>
    </mi-drawer>
  </div>
</template>

<script>
  import { computed, nextTick, onMounted, ref, watch } from 'vue'
  import MiDrawer from '@/packages/@mi-library/MiDrawer/MiDrawer.vue'
  import MiTabPanels from '@/packages/@mi-library/MiTabPanels/MiTabPanels.vue'
  import MiTabPanel from '@/packages/@mi-library/MiTabPanels/MiTabPanel.vue'
  import MiInnerLoading from '@/packages/@mi-library/MiInnerLoading/MiInnerLoading.vue'
  import MiSpinner from '@/packages/@mi-library/MiSpinner/MiSpinner.vue'
  import {
    ACTIVITY_CENTER_FEATURE_TAB_NAMES,
    ACTIVITY_CENTER_TAB_NAMES,
    getActivityCenterStatusTabs,
    isActivityWithinVisibleTimePeriod
  } from '@/constants/activityCenter'
  import { mapNotificationToActivity } from '@/utils/mappers/activityCenterNotificationMapper'
  import { getNotifications } from '@/api/rest/activity-center'
  import ActivityCenterHeader from './ActivityCenterHeader.vue'
  import ActivityCenterTabs from './ActivityCenterTabs.vue'
  import ActivityCenterTabContent from './ActivityCenterTabContent.vue'
  import ActivityCenterPreferences from './ActivityCenterPreferences.vue'

  export default {
    name: 'ActivityCenter',
    components: {
      MiDrawer,
      MiTabPanels,
      MiTabPanel,
      MiInnerLoading,
      MiSpinner,
      ActivityCenterHeader,
      ActivityCenterTabs,
      ActivityCenterTabContent,
      ActivityCenterPreferences
    },
    props: {
      notificationId: {
        type: String,
        default: ''
      }
    },
    emits: ['close', 'notifications-loaded'],
    setup(props, { emit }) {
      const activeTab = ref(ACTIVITY_CENTER_TAB_NAMES.ALL)
      const activeFeature = ref(ACTIVITY_CENTER_FEATURE_TAB_NAMES.ALL)
      const isPreferencesOpen = ref(false)

      const togglePreferences = () => {
        isPreferencesOpen.value = !isPreferencesOpen.value
      }

      const activities = ref([])
      const activitiesLoading = ref(false)
      const initiallyOpenSectionKeys = ref([])

      const activityCenterTabs = computed(() => (
        getActivityCenterStatusTabs(activities.value)
      ))

      const getResponsePayload = response => response?.data ?? response

      const getNotificationsFromResponse = response => {
        const payload = getResponsePayload(response)

        if (Array.isArray(payload?.elements)) {
          return payload.elements
        }

        if (Array.isArray(payload)) {
          return payload
        }

        return []
      }

      const getAllNotifications = async () => {
        const firstResponse = await getNotifications({
          page: 0
        })

        const firstPayload = getResponsePayload(firstResponse)
        const totalPages = firstPayload?.totalPages || 1

        if (totalPages === 1) {
          return getNotificationsFromResponse(firstResponse)
        }

        const remainingResponses = await Promise.all(
          Array.from(
            { length: totalPages - 1 },
            (_, index) => getNotifications({
              page: index + 1
            })
          )
        )

        return [firstResponse, ...remainingResponses]
          .flatMap(getNotificationsFromResponse)
      }

      const fetchActivities = async () => {
        activitiesLoading.value = true

        try {
          const notifications = await getAllNotifications()

          const mappedActivities = notifications
            .reduce((mappedList, notification) => {
              try {
                mappedList.push({
                  ...mapNotificationToActivity(notification),
                  read: notification.read
                })
              }
              catch (error) {
                console.warn(
                  'Skipping unsupported Activity Center notification:',
                  notification?.eventId,
                  error?.message
                )
              }

              return mappedList
            }, [])
            .filter(isActivityWithinVisibleTimePeriod)
            .sort((first, second) => (
              new Date(second.createdAt).getTime()
              - new Date(first.createdAt).getTime()
            ))

          initiallyOpenSectionKeys.value = [
            ...new Set(
              mappedActivities
                .filter(({ read }) => read === false)
                .flatMap(({ feature, status }) => [
                  `${ ACTIVITY_CENTER_TAB_NAMES.ALL }:${ feature }`,
                  `${ status }:${ feature }`
                ])
            )
          ]

          activities.value = mappedActivities

          emit('notifications-loaded')
        }
        catch (error) {
          console.error(
            'Failed to fetch Activity Center notifications:',
            error
          )

          activities.value = []
          initiallyOpenSectionKeys.value = []
        }
        finally {
          activitiesLoading.value = false
        }
      }

      const handleRetry = async () => {
        await fetchActivities()
      }

      const focusNotification = async () => {
        if (!props.notificationId) return

        const activity = activities.value.find(
          ({ id }) => id === props.notificationId
        )

        if (!activity) return

        activeTab.value = activity.status
        activeFeature.value = activity.feature

        await nextTick()

        const activityCard = document.getElementById(
          `activity-center-notification-${ activity.id }`
        )

        if (activityCard) {
          activityCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }

      watch(
        () => props.notificationId,
        async () => {
          await fetchActivities()
          await focusNotification()
        }
      )

      onMounted(async () => {
        await fetchActivities()
        await focusNotification()
      })

      return {
        activeTab,
        activeFeature,
        activities,
        activitiesLoading,
        activityCenterTabs,
        initiallyOpenSectionKeys,
        isPreferencesOpen,
        togglePreferences,
        handleRetry,
        ACTIVITY_CENTER_TAB_NAMES
      }
    }
  }
</script>

<style lang="scss">
    .activity-center-wrapper {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;

      /* stylelint-disable declaration-no-important */
      .q-drawer {
        top: 0 !important;
        height: 100vh !important;
      }
      /* stylelint-enable declaration-no-important */
    }

    .activity-center-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(67, 65, 65, .6);
      z-index: 2998;
    }
  </style>

  <style lang="scss" scoped>
    .activity-center {
      display: flex;
      flex-direction: column;
      height: 100%;

      &__content {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      &__panels {
        flex: 1;
        min-height: 0;

        ::v-deep(.q-tab-panels) {
          height: 100%;
        }

        ::v-deep(.q-tab-panel) {
          padding: 0;
          height: 100%;
        }
      }
    }
</style>
